import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API, authFetch } from "../App";
import {
  LayoutDashboard,
  FileText,
  Calculator,
  MessageSquare,
  Users,
  Settings,
  Shield,
  LogOut,
  Menu,
  X,
  Activity,
  UserCheck,
  TrendingUp,
  User,
  Mail,
  Tag,
  Percent,
} from "lucide-react";
import { Button } from "./ui/button";

const PortalLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [purchasedServices, setPurchasedServices] = useState([]);

  useEffect(() => {
    if (user?.role === "client" || user?.role === "client") {
      const fetchPayments = async () => {
        try {
          const res = await authFetch(`${API}/payments/my-payments`);
          if (res.ok) {
            const data = await res.json();
            const completed = data.filter(p => p.status === 'completed').map(p => p.service_type);
            setPurchasedServices(completed);
          }
        } catch (e) {
          console.error("Failed to fetch payments for sidebar", e);
        }
      };
      fetchPayments();
    }
  }, [user]);

  const getLinks = () => {
    const role = user?.role || "client";
    if (role === "super_admin") {
      return [
        { name: "Analytics", path: "/admin/analytics", icon: LayoutDashboard },
        { name: "Allocation", path: "/admin/allocation", icon: UserCheck },
        { name: "All Clients", path: "/admin/clients", icon: Users },
        { name: "Team Perf.", path: "/admin/team", icon: TrendingUp },
        { name: "Users", path: "/admin/users", icon: User },
        { name: "Inbox", path: "/admin/messages", icon: MessageSquare },
        { name: "Inquiries", path: "/admin/inquiries", icon: Mail },
        { name: "Services", path: "/admin/services", icon: Settings },
        { name: "Discounts", path: "/admin/discounts", icon: Tag },
        { name: "Rates", path: "/admin/custom-rates", icon: Percent }
      ];
    } else if (role === "team_member") {
      return [
        { name: "Team Dashboard", path: "/team", icon: LayoutDashboard },
        { name: "Messages", path: "/messages", icon: MessageSquare },
        { name: "Client Activity", path: "/team/activity", icon: Activity },
      ];
    } else {
      const clientLinks = [
        { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
        { name: "Messages", path: "/messages", icon: MessageSquare },
        { name: "Profiles", path: "/profiles", icon: Users },
        { name: "Services", path: "/package-selection", icon: Settings },
      ];
      
      const hasItr = purchasedServices.includes("itr_filing") || purchasedServices.includes("both");
      const hasAccounting = purchasedServices.includes("accounting") || purchasedServices.includes("both");

      if (hasItr) {
        clientLinks.splice(1, 0, { name: "ITR Filing", path: "/itr-filing", icon: FileText });
      }
      if (hasAccounting) {
        clientLinks.splice(hasItr ? 2 : 1, 0, { name: "Accounting", path: "/accounting", icon: Calculator });
      }
      
      return clientLinks;
    }
  };

  const links = getLinks();

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white">
      <div className="p-6 flex items-center gap-3">
        <span className="font-bold text-lg text-[var(--primary)] truncate">CA Ronak Patel</span>
      </div>

      <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
        {links.map((link) => {
          const isActive = location.pathname === link.path || 
            (location.pathname.startsWith(link.path + '/') && link.path !== '/team' && !link.path.startsWith('/admin/'));
          const Icon = link.icon;
          return (
            <button
              key={link.path}
              data-testid={`sidebar-${link.name.toLowerCase().replace(' ', '-')}`}
              onClick={() => {
                navigate(link.path);
                setMobileOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 font-bold text-sm ${
                isActive
                  ? "bg-[#145886] text-white shadow-md"
                  : "text-[#374151] hover:bg-white hover:shadow-sm"
              }`}
            >
              <Icon className="w-5 h-5" />
              {link.name}
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-[rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-3 mb-4 px-2">
          {user?.picture ? (
            <img src={user.picture} alt="User" className="w-10 h-10 rounded-full border-2 border-white shadow-sm" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#145886] text-white flex items-center justify-center font-bold text-lg shadow-sm">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm text-[#374151] truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-[#6B7280] truncate">{user?.email}</p>
          </div>
        </div>

        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-red-600 hover:bg-red-50 font-bold text-sm transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
        
        {/* Version Manager Display */}
        <div className="mt-4 text-center">
          <p className="text-[10px] text-gray-400 font-medium">
            Version {require('../version.json').version} (Build {require('../version.json').build})
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[var(--background-primary)] overflow-hidden font-sans">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-72 flex-col fixed inset-y-0 z-50 border-r border-[rgba(0,0,0,0.05)] shadow-[4px_0_24px_rgba(0,0,0,0.02)]">
        <SidebarContent />
      </aside>

      {/* Mobile Drawer Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden" 
          onClick={() => setMobileOpen(false)} 
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out md:hidden ${mobileOpen ? "translate-x-0" : "-translate-x-full"} border-r border-[rgba(0,0,0,0.05)] shadow-2xl`}>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col md:ml-72 h-screen overflow-hidden">
        {/* Mobile Header */}
        <header className="md:hidden flex items-center justify-between p-4 bg-white shadow-sm z-30">
          <div className="flex items-center gap-3">
            <span className="font-bold text-[var(--primary)]">{title || "Portal"}</span>
          </div>
          <button onClick={() => setMobileOpen(true)} className="p-2 bg-white rounded-xl shadow-sm">
            <Menu className="w-5 h-5 text-[var(--text-main)]" />
          </button>
        </header>

        {/* Desktop Header Top Bar (Optional, can just show title) */}
        <header className="hidden md:flex items-center justify-between px-8 py-6">
          <h1 className="heading-style-h1 text-2xl" style={{ color: "#145886" }}>{title || "Portal"}</h1>
        </header>

        {/* Scrollable Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-8 pb-24 min-w-0">
          <div className="max-w-6xl mx-auto min-w-0">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PortalLayout;
